import { Component, computed, DestroyRef, inject, OnInit, output, signal, viewChild } from '@angular/core';
import { LocalizationPipe } from '@abp/ng.core';
import { Confirmation, ToasterService } from '@abp/ng.theme.shared';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, finalize, of, Subject, Subscription, switchMap, tap, timer } from 'rxjs';
import { DEFAULT_TOASTER_LIFE, ZnConfirmation, ZnMultiSelect } from '@zendra/shared';
import { IModalContent } from 'projects/shared/src/lib/zn-modal-wrapper/interfaces/IModalContent';
import {
  InventoryProjectionLookupDto,
  InventoryProjectionOrchestratorService,
  InventoryProjectionStandDto,
} from 'orchestrator';

type SelectionValue = string | string[] | null;
type SelectOption = { key: string; value: string };
type StandRequest = { regionIds: number[]; propertyIds: number[]; requestId: number; debounce: boolean };

@Component({
  selector: 'lib-inventory-projection',
  standalone: true,
  imports: [LocalizationPipe, ZnMultiSelect],
  templateUrl: './inventory-projection.html',
  styleUrl: './inventory-projection.scss',
})
export class InventoryProjection implements OnInit, IModalContent {
  private readonly service = inject(InventoryProjectionOrchestratorService);
  private readonly toaster = inject(ToasterService);
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly regionSelect = viewChild('regionSelect', { read: ZnMultiSelect });
  protected readonly propertySelect = viewChild('propertySelect', { read: ZnMultiSelect });
  protected readonly standSelect = viewChild('standSelect', { read: ZnMultiSelect });

  protected readonly standSelectionRequiredKey = 'Orchestrator::App:InventoryProjectionStandSelectionRequired';

  protected readonly regionOptions = signal<SelectOption[]>([]);
  protected readonly propertyOptions = signal<SelectOption[]>([]);
  protected readonly standOptions = signal<SelectOption[]>([]);

  protected readonly selectedRegionIds = signal<number[]>([]);
  protected readonly selectedPropertyIds = signal<number[]>([]);
  protected readonly selectedStandLocationIds = signal<number[]>([]);

  protected readonly loadingRegions = signal(false);
  protected readonly loadingProperties = signal(false);
  protected readonly loadingStands = signal(false);
  protected readonly standsLoaded = signal(false);
  private readonly executing = signal(false);
  private readonly formChanged = signal(false);

  completed = output<void>();
  loading = this.executing;

  private propertiesRequestId = 0;
  private standsRequestId = 0;
  private readonly standRequests = new Subject<StandRequest | null>();
  private propertiesSubscription?: Subscription;

  constructor() {
    this.standRequests.pipe(
      // The outer switch cancels immediately, even while the next selection is
      // being debounced; stale HTTP calls cannot accumulate behind that delay.
      switchMap(request => request ? this.requestStands(request) : EMPTY),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  canSave = computed(() =>
    this.selectedStandLocationIds().length > 0 &&
    !this.loadingRegions() &&
    !this.loadingProperties() &&
    !this.loadingStands() &&
    !this.executing()
  );

  protected readonly showStandSelectionError = computed(() =>
    this.standsLoaded() && !this.loadingStands() && this.selectedStandLocationIds().length === 0
  );

  ngOnInit(): void {
    this.loadRegions();
  }

  protected onRegionSelectionChange(value: SelectionValue): void {
    const regionIds = this.readSelectedIds(value, this.regionSelect(), this.regionOptions());
    this.formChanged.set(true);
    this.selectedRegionIds.set(regionIds);
    this.loadProperties(regionIds);
  }

  protected onPropertySelectionChange(value: SelectionValue): void {
    const propertyIds = this.readSelectedIds(value, this.propertySelect(), this.propertyOptions());
    this.formChanged.set(true);
    this.selectedPropertyIds.set(propertyIds);
    this.loadStands(this.selectedRegionIds(), propertyIds, true);
  }

  protected onStandSelectionChange(value: SelectionValue): void {
    this.formChanged.set(true);
    this.selectedStandLocationIds.set(this.readSelectedIds(value, this.standSelect(), this.standOptions()));
  }

  confirm(): void {
    if (!this.canSave()) {
      return;
    }

    this.executing.set(true);
    this.setSelectsDisabled(true);

    this.service.execute({ standLocationIds: [...this.selectedStandLocationIds()] })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.executing.set(false);
          this.setSelectsDisabled(false);
        }),
      )
      .subscribe({
        next: result => {
          this.formChanged.set(false);
          this.toaster.success(result.message, null, { life: DEFAULT_TOASTER_LIFE });
          // Acceptance closes the modal; data has not finished processing yet.
          this.activeModal.close(result);
        },
        error: error => console.error(error),
      });
  }

  dismiss(): void {
    if (this.executing()) {
      return;
    }

    if (!this.formChanged()) {
      this.activeModal.dismiss();
      return;
    }

    this.confirmationService.showCancelConfirmation().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(status => {
      if (status === Confirmation.Status.confirm) {
        this.activeModal.dismiss();
      }
    });
  }

  private loadRegions(): void {
    this.loadingRegions.set(true);

    this.service.getRegions()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loadingRegions.set(false)),
      )
      .subscribe({
        next: regions => {
          const options = this.toLookupOptions(regions);
          const regionIds = this.getOptionIds(options);

          this.regionOptions.set(options);
          this.selectedRegionIds.set(regionIds);
          this.syncSelect(this.regionSelect, regionIds);
          this.loadProperties(regionIds);
        },
        error: error => console.error(error),
      });
  }

  private loadProperties(regionIds: number[]): void {
    const requestId = ++this.propertiesRequestId;
    this.propertiesSubscription?.unsubscribe();

    this.clearProperties();
    this.standsRequestId++;
    this.standRequests.next(null);
    this.clearStands();
    this.loadingStands.set(false);
    this.standsLoaded.set(false);

    if (!regionIds.length) {
      this.loadingProperties.set(false);
      this.standsLoaded.set(true);
      return;
    }

    this.loadingProperties.set(true);

    this.propertiesSubscription = this.service.getProperties({ regionIds })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (requestId === this.propertiesRequestId) {
            this.loadingProperties.set(false);
          }
        }),
      )
      .subscribe({
        next: properties => {
          if (requestId !== this.propertiesRequestId) {
            return;
          }

          const options = this.toLookupOptions(properties);
          const propertyIds = this.getOptionIds(options);

          this.propertyOptions.set(options);
          this.selectedPropertyIds.set(propertyIds);
          this.syncSelect(this.propertySelect, propertyIds);
          this.loadStands(regionIds, propertyIds);
        },
        error: error => {
          if (requestId === this.propertiesRequestId) {
            this.standsLoaded.set(true);
          }
          console.error(error);
        },
      });
  }

  private loadStands(regionIds: number[], propertyIds: number[], debounce = false): void {
    const requestId = ++this.standsRequestId;
    this.standRequests.next(null);

    this.clearStands();
    this.standsLoaded.set(false);

    if (!regionIds.length || !propertyIds.length) {
      this.loadingStands.set(false);
      this.standsLoaded.set(true);
      return;
    }

    this.loadingStands.set(true);
    this.standRequests.next({ regionIds: [...regionIds], propertyIds: [...propertyIds], requestId, debounce });
  }

  private requestStands({ regionIds, propertyIds, requestId, debounce }: StandRequest) {
    return (debounce ? timer(300) : of(0)).pipe(
      switchMap(() => this.service.getStands({ regionIds, propertyIds })),
      tap(stands => {
        if (requestId === this.standsRequestId) {
          const options = this.toStandOptions(stands);
          const standLocationIds = this.getOptionIds(options);

          this.standOptions.set(options);
          this.selectedStandLocationIds.set(standLocationIds);
          this.syncSelect(this.standSelect, standLocationIds);
        }
      }),
      catchError(error => {
        console.error(error);
        return EMPTY;
      }),
      finalize(() => {
        if (requestId === this.standsRequestId) {
          this.loadingStands.set(false);
          this.standsLoaded.set(true);
        }
      }),
    );
  }

  private clearProperties(): void {
    this.propertyOptions.set([]);
    this.selectedPropertyIds.set([]);
    this.syncSelect(this.propertySelect, []);
  }

  private clearStands(): void {
    this.standOptions.set([]);
    this.selectedStandLocationIds.set([]);
    this.syncSelect(this.standSelect, []);
  }

  private syncSelect(select: () => ZnMultiSelect | undefined, selectedIds: number[]): void {
    queueMicrotask(() => {
      const component = select();
      component?.reset();
      component?.writeValue(selectedIds.map(String));
    });
  }

  private readSelectedIds(value: SelectionValue, select: ZnMultiSelect | undefined, options: SelectOption[]): number[] {
    const rawValues = select ? select.draftSelected() : this.normalizeSelectionValue(value);
    const validOptionIds = new Set(options.map(option => option.key));

    return rawValues
      .filter(id => validOptionIds.has(String(id)))
      .map(Number)
      .filter(Number.isFinite);
  }

  private normalizeSelectionValue(value: SelectionValue): string[] {
    if (Array.isArray(value)) {
      return value.map(String);
    }

    if (value === null || value === undefined || value === '') {
      return [];
    }

    return [String(value)];
  }

  private toLookupOptions(items: InventoryProjectionLookupDto[]): SelectOption[] {
    return items
      .filter(item => item.id !== undefined && item.name !== undefined)
      .map(item => ({ key: String(item.id), value: item.name! }));
  }

  private toStandOptions(items: InventoryProjectionStandDto[]): SelectOption[] {
    return items
      .filter(item => item.locationId !== undefined && item.name !== undefined)
      .map(item => ({ key: String(item.locationId), value: item.name! }));
  }

  private getOptionIds(options: SelectOption[]): number[] {
    return options.map(option => Number(option.key)).filter(Number.isFinite);
  }

  private setSelectsDisabled(disabled: boolean): void {
    this.regionSelect()?.setDisabledState(disabled);
    this.propertySelect()?.setDisabledState(disabled);
    this.standSelect()?.setDisabledState(disabled);
  }
}
